import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FormattedMessage } from "react-intl";
import { CardContent, CircularProgress, Grid, Typography, makeStyles } from "@material-ui/core";
import PageContainer, { CardPageItem, PageItem } from "../custom/Page";
import HeaderPageItem from "../custom/HeaderPageItem";
import { searchInvites } from "../actions/Invite";
import { showErrorMessage } from "../actions/Alert";
import { resolveErrorMessage } from "../util";
import DebouncedField from "./components/DebouncedField";
import { InviteListBySituationWithAccordion } from "./components/Management/InvitesBySituation";

const useStyles = makeStyles((theme) => ({
  filterCard: {
    marginBottom: theme.spacing(1),
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
}));

const AdminInvites = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [filter, setFilter] = useState({ searchByText: "" });
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchInvites = useCallback(async (filterParams) => {
    setLoading(true);
    try {
      const data = await searchInvites({
        ...(filterParams.searchByText && { nameOrEmail: filterParams.searchByText }),
      });
      setInvites(data?.content || []);
    } catch (error) {
      dispatch(showErrorMessage(resolveErrorMessage(error)));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchInvites({ searchByText: "" });
  }, [fetchInvites]);

  const handleFilterChange = useCallback((name, value) => {
    setFilter((prev) => {
      const nextFilter = { ...prev, [name]: value };
      fetchInvites(nextFilter);
      return nextFilter;
    });
  }, [fetchInvites]);

  const hasFilters = filter.searchByText?.length > 0;

  const invitesByStatus = useMemo(() => {
    const groups = { ACTIVE: [], USED: [], EXPIRED: [], REVOKED: [] };
    invites.forEach((invite) => {
      (groups[invite.status] || groups.ACTIVE).push(invite);
    });
    return groups;
  }, [invites]);

  const handleInviteChanged = (updatedInvite) => {
    setInvites((prev) => prev.map((invite) => (invite.id === updatedInvite.id ? { ...invite, ...updatedInvite } : invite)));
  };

  return (
    <PageContainer>
      <HeaderPageItem title="app.invites" showBackButton destination="/more" />
      <PageItem>
        <CardPageItem raised margin={1} className={classes.filterCard}>
          <CardContent>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12}>
                <DebouncedField
                  id="searchByText"
                  name="searchByText"
                  label={<FormattedMessage id="app.search.nameemail" />}
                  value={filter.searchByText}
                  onDebouncedChange={handleFilterChange}
                />
              </Grid>
            </Grid>
          </CardContent>
        </CardPageItem>

        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : invites.length === 0 ? (
          <CardPageItem raised margin={1}>
            <CardContent>
              <Typography align="center">
                <FormattedMessage id="app.invite.empty" />
              </Typography>
            </CardContent>
          </CardPageItem>
        ) : (
          <>
            <InviteListBySituationWithAccordion
              data={invitesByStatus.ACTIVE}
              textBar="app.invite.situation.pending"
              defaultOpen
              showAgent
              canManage={true}
              onInviteChanged={handleInviteChanged}
            />
            <InviteListBySituationWithAccordion
              data={invitesByStatus.USED}
              textBar="app.invite.situation.used"
              defaultOpen={hasFilters}
              showAgent
              canManage={true}
              onInviteChanged={handleInviteChanged}
            />
            <InviteListBySituationWithAccordion
              data={invitesByStatus.EXPIRED}
              textBar="app.invite.situation.expired"
              defaultOpen={hasFilters}
              showAgent
              canManage={true}
              onInviteChanged={handleInviteChanged}
            />
            <InviteListBySituationWithAccordion
              data={invitesByStatus.REVOKED}
              textBar="app.invite.situation.revoked"
              defaultOpen={hasFilters}
              showAgent
              canManage={true}
              onInviteChanged={handleInviteChanged}
            />
          </>
        )}
      </PageItem>
    </PageContainer>
  );
};

export default AdminInvites;
